import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileDown, Loader2, Mail, Send, ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTermValue, fmtDeadline } from "./negotiationShared";
import { downloadPdf } from "./negotiationEvents";

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

export function CounterOfferExport({ open, onOpenChange, neg, version, summary, catalog, onSent }) {
  const { tf, locale, lang } = useLang();
  const { user } = useAuth() || {};
  const [mode, setMode] = useState("export");
  const byKey = useMemo(() => Object.fromEntries((catalog || []).map((c) => [c.term_key, c])), [catalog]);
  const terms = version?.terms || [];
  const [checked, setChecked] = useState(() => Object.fromEntries(terms.map((t) => [t.term_key, true])));
  const [note, setNote] = useState("");
  const [validity, setValidity] = useState("");
  const [busy, setBusy] = useState(false);
  // composer state
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [resendPrompt, setResendPrompt] = useState(null);
  const tokenRef = useRef(uid());

  const agentName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "";
  const athlete = summary?.athlete || "";

  useEffect(() => {
    if (mode !== "compose") return;
    if (!subject) {
      setSubject(lang === "en"
        ? `Counter-offer — ${athlete || "Athlete"} — ${neg.counterparty || ""}`.trim()
        : `${tfr("Contre-offre")} — ${athlete || tfr("Athlète")} — ${neg.counterparty || ""}`.trim());
    }
    if (!message) {
      const vu = version?.valid_until ? fmtDeadline(version.valid_until, locale) : null;
      const fr = `Bonjour,\n\nVeuillez trouver ci-joint notre contre-offre concernant ${athlete || tfr("notre athlète")}.\nLe document reprend les conditions proposées à ce stade de la négociation.${vu ? `\n\nCette proposition est valable jusqu'au ${vu}.` : ""}\n\nBien cordialement,\n${agentName}`;
      const en = `Hello,\n\nPlease find attached our counter-offer regarding ${athlete || "our athlete"}.\nThe attached document reflects the terms proposed at this stage of the negotiation.${vu ? `\n\nThis offer is valid until ${vu}.` : ""}\n\nBest regards,\n${agentName}`;
      setMessage(lang === "en" ? en : fr);
    }
    if (suggestion === null && neg.counterparty) {
      api.get("/clubs").then((r) => {
        const clubs = r.data?.clubs || r.data || [];
        const hit = (Array.isArray(clubs) ? clubs : []).find(
          (cl) => (cl.name || "").toLowerCase() === (neg.counterparty || "").toLowerCase() && cl.contact_email);
        setSuggestion(hit ? hit.contact_email : false);
      }).catch(() => setSuggestion(false));
    }
  }, [mode]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!version) return null;
  const selectedKeys = terms.filter((t) => checked[t.term_key]).map((t) => t.term_key);
  const label = (t) => (byKey[t.term_key] && tf(byKey[t.term_key].label)) || t.custom_label || t.term_key;
  const val = (t) => fmtTermValue(t.vtype, t.currency, ["money", "number", "percent"].includes(t.vtype) ? t.amount : t.vtype === "bool" ? t.value_bool : t.value_text, locale, tf);
  const canSendByEmail = ["agent", "athlete"].includes(version.proposed_by);
  const fname = `Athlynx_Counter_Offer_V${version.version_number}.pdf`;

  const exportPdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(api, `/negotiations/${neg.id}/versions/${version.id}/counter-offer-pdf`,
        { term_keys: selectedKeys, note: note.trim() || null, validity_date: validity || null, lang },
        fname);
      toast.success(tf("PDF de contre-offre téléchargé"));
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  const send = async (force = false) => {
    setBusy(true);
    try {
      const parse = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
      await api.post(`/negotiations/${neg.id}/versions/${version.id}/send`, {
        to: to.trim(), cc: parse(cc), bcc: parse(bcc), subject: subject.trim(), message: message.trim(),
        term_keys: selectedKeys, lang, client_token: tokenRef.current, force,
      });
      toast.success(tf("Contre-offre envoyée"));
      setResendPrompt(null);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "already_sent") {
        setResendPrompt(d);
      } else {
        toast.error(typeof d === "string" ? d : tf("Erreur"));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="counter-offer-export-dialog">
        {mode === "compose" ? (
          <>
            <DialogTitle className="flex items-center gap-2">
              <button type="button" onClick={() => setMode("export")} className="text-muted-foreground hover:text-foreground" data-testid="co-compose-back" aria-label={tf("Retour")}>
                <ArrowLeft className="h-4 w-4" />
              </button>
              {tf("Envoyer la contre-offre")} — V{version.version_number}
            </DialogTitle>
            <DialogDescription>{tf("Seul le PDF de contre-offre approuvé et votre message seront envoyés — aucune donnée interne.")}</DialogDescription>

            <div className="space-y-2.5" data-testid="co-composer">
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{tf("Destinataire")}</h4>
                <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="contact@club.com" className="h-9" data-testid="co-to-input" />
                {suggestion && suggestion !== to && (
                  <button type="button" className="mt-1 text-[11px] text-red-500 hover:underline" onClick={() => setTo(suggestion)} data-testid="co-suggestion-btn">
                    {tf("Utiliser le contact du club")} : {suggestion}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">CC ({tf("optionnel")})</h4>
                  <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="a@x.com, b@y.com" className="h-9" data-testid="co-cc-input" />
                </div>
                <div>
                  <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">CCI ({tf("optionnel")})</h4>
                  <Input value={bcc} onChange={(e) => setBcc(e.target.value)} className="h-9" data-testid="co-bcc-input" />
                </div>
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{tf("Sujet")}</h4>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" data-testid="co-subject-input" />
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{tf("Message")}</h4>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} className="text-sm" data-testid="co-message-input" />
              </div>
              <div className="rounded-xl border border-border/60 dark:border-white/[0.06] p-3 text-xs space-y-1" data-testid="co-attachment">
                <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground flex items-center gap-1.5"><FileText className="h-3 w-3" /> {tf("Pièce jointe")}</h4>
                <p className="font-medium">{fname}</p>
                <p className="text-muted-foreground">V{version.version_number} · {selectedKeys.length} {tf("conditions incluses")} · {tf("Athlète")} : {athlete || "—"} · {neg.counterparty || "—"}</p>
                {version.valid_until && <p className="text-muted-foreground">{tf("Valable jusqu'au")} : {fmtDeadline(version.valid_until, locale)}</p>}
                <p className="text-muted-foreground">{tf("Réponse à")} : {user?.email || "—"}</p>
              </div>
              {resendPrompt && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2" data-testid="co-resend-warning">
                  <p>{tf("Cette version a déjà été envoyée à ce destinataire le")} {resendPrompt.last_sent_at ? fmtDeadline(resendPrompt.last_sent_at, locale) : "—"}.</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => setResendPrompt(null)} data-testid="co-resend-cancel">{tf("Annuler")}</Button>
                    <Button size="sm" className="h-8" disabled={busy} onClick={() => send(true)} data-testid="co-resend-confirm">{tf("Envoyer à nouveau")}</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMode("export")}>{tf("Retour")}</Button>
              <Button size="sm" disabled={busy || !to.trim() || !subject.trim() || !message.trim() || selectedKeys.length === 0 || !!resendPrompt}
                onClick={() => send(false)} data-testid="co-send-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                {tf("Envoyer")}
              </Button>
            </div>
          </>
        ) : (
          <>
        <DialogTitle>{tf("Exporter la contre-offre en PDF")} — V{version.version_number}</DialogTitle>
        <DialogDescription>{tf("Seules les informations sélectionnées ci-dessous apparaîtront dans le document. Les objectifs internes, statuts et notes internes ne sont jamais inclus.")}</DialogDescription>

        <div>
          <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1.5">{tf("Conditions à inclure")}</h4>
          <div className="space-y-1.5">
            {terms.map((t) => (
              <label key={t.term_key} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`co-term-${t.term_key}`}>
                <Checkbox checked={!!checked[t.term_key]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [t.term_key]: !!v }))} />
                <span className="flex-1">{label(t)}</span>
                <span className="font-medium">{val(t)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{tf("Valable jusqu'au")} ({tf("optionnel")})</h4>
            <Input type="date" value={validity} onChange={(e) => setValidity(e.target.value)} className="h-9" data-testid="co-validity-input" />
          </div>
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{tf("Note partageable")} ({tf("optionnel")})</h4>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="text-sm"
            placeholder={tf("Visible par la contrepartie — n'y mettez aucune information interne.")} data-testid="co-note-input" />
        </div>

        <div className="rounded-xl border border-border/60 dark:border-white/[0.06] p-3 text-xs space-y-1" data-testid="co-preview">
          <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{tf("Aperçu du contenu")}</h4>
          <p><span className="text-muted-foreground">{tf("Athlète")} :</span> {summary?.athlete || "—"} · <span className="text-muted-foreground">{tf("Contrepartie")} :</span> {neg.counterparty || "—"}</p>
          <p><span className="text-muted-foreground">{tf("Conditions")} :</span> {selectedKeys.length ? terms.filter((t) => checked[t.term_key]).map((t) => label(t)).join(", ") : tf("Aucun élément")}</p>
          {validity && <p><span className="text-muted-foreground">{tf("Valable jusqu'au")} :</span> {new Date(validity).toLocaleDateString(locale)}</p>}
          {note.trim() && <p><span className="text-muted-foreground">{tf("Note")} :</span> {note.trim()}</p>}
          <p><span className="text-muted-foreground">{tf("Contact agent")} :</span> {summary?.owner || "—"}</p>
        </div>

        <div className="flex justify-end gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          {canSendByEmail && (
            <Button variant="outline" size="sm" disabled={selectedKeys.length === 0} onClick={() => setMode("compose")} data-testid="co-open-composer-btn">
              <Mail className="h-3.5 w-3.5 mr-1.5" /> {tf("Envoyer la contre-offre")}
            </Button>
          )}
          <Button size="sm" disabled={busy || selectedKeys.length === 0} onClick={exportPdf} data-testid="co-export-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
            {tf("Télécharger le PDF")}
          </Button>
        </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
